@if (
  {
    running: running$ | async,
    hasCommutationEncoder: hasCommutationEncoder$ | async,
    commutationOffsetAppliedPercentOfRatedTorqueParameter:
      commutationOffsetAppliedPercentOfRatedTorqueParameter$ | async,
    commutationAngleOffsetValid: commutationAngleOffsetValid$ | async,
    commutationAngleOffsetParameter: commutationAngleOffsetParameter$ | async,
    motorPhasesInvertedParameter: motorPhasesInvertedParameter$ | async,
    stepValueDifference: stepValueDifference$ | async
  };
  as data
) {
  <div class="row">
    <div class="col">
      <div class="row">
        <div class="col">
          <h4>Offset Detection</h4>
          <p>
            This process identifies the relationship between the motor's electrical angle and mechanical angle by
            detecting the phase order and commutation angle offset.<br />Use the default percentage of rated torque,
            increasing it only if the motor's rotation is uneven.
          </p>
          <app-doc-feeling-lucky-link qNew="COMMUTATION_OFFSET_DETECTION" qOld="offset-detection" [device]="device">
            For additional information about this feature, please visit our documentation site.
          </app-doc-feeling-lucky-link>
        </div>
      </div>
      <!-- Missing commutation encoder notification -->
      @if (data.hasCommutationEncoder === false) {
        <div>
          <div class="alert alert-warning my-2 shadow-sm" role="alert">
            <i class="bi-exclamation-triangle-fill me-1"></i>
            Device has no encoder configuration used for commutation. Running the offset detection procedure will fail.
          </div>
        </div>
      }
      @if (!data.commutationAngleOffsetValid) {
        <div class="alert alert-danger">
          <i class="bi bi-exclamation-triangle-fill me-1"></i>
          The commutation offset value is invalid. Please start the offset detection.
        </div>
      }
      <div class="row mt-4 mb-5 justify-content-md-center">
        <div class="col col-lg-4">
          <ng-container
            [ngTemplateOutlet]="commutationOffsetTemplate"
            [ngTemplateOutletContext]="{
              $implicit: {
                status: data.running ? 'running' : data.commutationAngleOffsetValid ? 'success' : 'error',
                value: data.commutationAngleOffsetParameter.value
              }
            }"
          ></ng-container>
        </div>
        <div class="col col-lg-4">
          <div class="card rounded-2">
            <div class="card-body">
              <h5 class="card-title fs-6 fw-normal text-body-secondary">Motor Phases Inverted</h5>
              <p class="card-text fs-5 fw-bold">
                {{ data.running ? '-' : (data.motorPhasesInvertedParameter | parameterValueDisplay) }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Offset detection slider -->
      <div class="row mt-3 mb-5">
        <div class="col">
          <h5 class="mb-4" id="commutationLabel">Applied percent of rated torque</h5>
          <input
            #commutationOffsetAppliedPercentOfRatedTorque
            type="range"
            min="0"
            max="100"
            step="1"
            [ngModel]="data.commutationOffsetAppliedPercentOfRatedTorqueParameter?.value"
            (change)="
              onChangeCommutationOffsetAppliedPercentOfRatedTorque(commutationOffsetAppliedPercentOfRatedTorque.value)
            "
            #rangeInput
            aria-describedby="commutationLegend"
            aria-label="commutationLabel"
            [disabled]="data.running"
          />
          <div id="commutationLegend" class="d-flex justify-content-between">
            <span class="text-body-secondary">0% (Min)</span>
            <span class="fw-bold"> {{ rangeInput.value || 0 }}%</span>
            <span class="text-body-secondary">(Max) 100%</span>
          </div>
        </div>
      </div>
      <app-parameter-form
        #pf
        [parameters$]="commutationParameters$"
        (apply)="onApply($event)"
        (formSubmit)="onApply($event.value)"
      ></app-parameter-form>
      @if ((isBrakeConfigured$ | async) && (expertMode$ | async)) {
        <div class="mt-2">
          <app-parameter-form [parameters$]="brakeWaitTimeParameters$" [showApplyButton]="false" #tf />
        </div>
      }
      <!-- Offset detection progress -->
      <h5 class="mb-4 mt-5">Progress</h5>
      @if (data.stepValueDifference.length > 0) {
        <div class="alert alert-warning mb-2">
          <i class="bi bi-exclamation-triangle-fill"></i>
          The entered motor values deviate significantly from the measured values. Please ensure that the motor values
          are correct.
          <table class="table table-borderless table-sm mt-2 mb-0 text-warning" style="--table-bg: transparent">
            <thead>
              <tr>
                <th>Step</th>
                <th class="text-end nowrap">Parameter value</th>
                <th class="text-end nowrap">Measured value</th>
              </tr>
            </thead>
            <tbody>
              @for (diff of data.stepValueDifference; track diff.id) {
                <tr>
                  <td style="width: 50%">{{ diff.step.label }}</td>
                  <td style="width: 20%" class="fw-bold text-danger text-end">
                    {{ diff.inputValue }} {{ diff.inputUnit }}
                  </td>
                  <td style="width: 30%" class="text-end">
                    {{ diff.step.value | formatOffsetDetectionStepValue: diff.id }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
      <app-progress-steps
        [progressSteps]="formattedValueSteps$ | async"
        [isRunning]="data.running"
        [disabled]="isMotionPrevented$ | async"
        (run)="onStartOffsetDetection($event)"
      ></app-progress-steps>
      <div class="text-end">
        <span appMotionPreventedTooltip>
          @if (!data.running) {
            <button
              type="button"
              class="btn btn-primary text-nowrap"
              (click)="onStartOffsetDetection()"
              [disabled]="isMotionPrevented$ | async"
            >
              START OFFSET DETECTION
            </button>
          }
        </span>
        @if (data.running) {
          <span class="me-3 animate__animated animate__slower animate__flash animate__infinite text-danger"
            >Offset detection is running</span
          >
        }
        @if (data.running) {
          <button type="button" class="btn btn-outline-danger text-nowrap" (click)="onStopOffsetDetection()">
            STOP OFFSET DETECTION
          </button>
        }
      </div>
    </div>
  </div>
}

<!-- Templates -->

<ng-template #commutationOffsetTemplate let-data>
  <div
    class="card rounded-2"
    [ngClass]="{
      'text-body-secondary': data.status === 'running',
      'text-success border-success': data.status === 'success'
    }"
  >
    <div class="card-body">
      <h5 class="card-title fs-6 fw-normal">Measured Commutation Offset</h5>
      <p class="card-text fs-5 fw-bold animate__animated" [class.animate__flash]="data.status !== 'running'">
        @switch (data.status) {
          @case ('running') {
            -
          }
          @default {
            {{ data.value }} | {{ data.value | commutationAngleOffsetElectricalDegrees }}
          }
        }
      </p>
    </div>
  </div>
</ng-template>
